import { IssuePickerOption } from "~/components/issueDetailsPage/IssuePickerOption"
import { formatDistanceToNow } from "date-fns"
import { CheckIcon, UsersIcon } from "lucide-react"
import { EntityIdentity } from "~/components/EntityIdentity"
import { CommentComposer, CommentContent } from "~/components/activity/Comments"
import { IssueDueDatePicker } from "~/components/issueDetailsPage/IssueDueDatePicker"
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from "~/components/ui/popover"
import {
	Command,
	CommandInput,
	CommandList,
	CommandGroup,
	CommandItem,
	CommandEmpty
} from "~/components/ui/command"
import { textToEditorContent } from "~/lib/utils"
import { ProjectStatusIcon } from "~/components/icons/project-status"
import type { useProjectPractice } from "~/hooks/useProjectPractice"
import { ExternalLinkIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react"
import { type ReactNode, useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { AppHeader } from "~/components/AppHeader"
import { Link } from "~/components/Link"
import { DetailsLayout } from "~/components/details/DetailsLayout"
import { DetailsRow, DetailsSection } from "~/components/details/DetailsSection"
import {
	IssueSetting,
	issueSettingAssignees
} from "~/components/issueDetailsPage/IssueSetting"
import { IssueRow } from "~/components/issuePage/IssueRow"
import { IssueCreateDialog } from "~/components/screens/IssueCreateDialog"
import {
	EmptyState,
	ErrorState,
	LoadingState
} from "~/components/screens/StateScreen"
import { UnsavedChanges } from "~/components/UnsavedChanges"
import { useWorkspace, useWorkspaceTab } from "~/components/workspace/Workspace"
import { Button, TooltipButton } from "~/components/ui/button"
import { Card } from "~/components/ui/card"
import { Input } from "~/components/ui/input"
import { Progress } from "~/components/ui/progress"
import { Textarea } from "~/components/ui/textarea"
import { usePermission } from "~/hooks/usePermission"
import { useTeam } from "~/hooks/useTeam"
import { IssueStatus, ProjectStatus, projectStatusValues } from "~/lib/enums"
import { confirmAction } from "~/lib/native-dialog"
import { TeamPermissions } from "~/lib/permissions"
import { projectSchema } from "~/lib/schemas"
import { enumFormat, fancyIssueId } from "~/lib/utils"
import { api, type RouterInputs } from "~/trpc/react"

export default function ProjectDetailsPage({
	practice,
	tutorialPrompt,
	tutorialActions
}: {
	practice?: ReturnType<typeof useProjectPractice>
	tutorialPrompt?: ReactNode
	tutorialActions?: ReactNode
}) {
	const params = useParams()
	const projectId = practice?.project.id ?? params.projectId
	const liveTeam = useTeam()
	const { members, orgSlug, isLoading } = liveTeam
	const team =
		practice && liveTeam.team
			? {
					...liveTeam.team,
					id: "practice-team",
					name: "Biteclub",
					slug: "bite",
					enableProjects: true
				}
			: liveTeam.team
	const { hasTeamPermission } = usePermission()
	const navigate = useNavigate()
	const workspace = useWorkspace()
	const workspaceTab = useWorkspaceTab()
	const utils = api.useUtils()
	const [draft, setDraft] = useState<RouterInputs["projects"]["save"] | null>(
		null
	)
	const projectQuery = api.projects.get.useQuery(
		{ teamId: team?.id ?? "", id: projectId ?? "" },
		{ enabled: !practice && !!team?.enableProjects && !!projectId }
	)
	const issuesQuery = api.issues.getTeamIssues.useQuery(
		{ teamId: team?.id ?? "", filter: { projectId } },
		{ enabled: !practice && !!team?.enableProjects && !!projectId }
	)
	const allIssuesQuery = api.issues.getTeamIssues.useQuery(
		{ teamId: team?.id ?? "" },
		{
			enabled:
				!practice &&
				!!team?.enableProjects &&
				!!projectId &&
				hasTeamPermission(TeamPermissions.EDIT_ISSUES)
		}
	)
	const project = {
		...projectQuery,
		data: practice?.project ?? projectQuery.data,
		isLoading: !practice && projectQuery.isLoading
	}
	const issues = {
		...issuesQuery,
		data: practice
			? {
					issues: practice.issues.filter(
						(issue) => issue.projectId === projectId
					)
				}
			: issuesQuery.data,
		isLoading: !practice && issuesQuery.isLoading
	}
	const allIssues = {
		...allIssuesQuery,
		data: practice ? { issues: practice.issues } : allIssuesQuery.data,
		isLoading: !practice && allIssuesQuery.isLoading
	}
	const [updateContent, setUpdateContent] = useState(() =>
		textToEditorContent(null)
	)
	const [composerKey, setComposerKey] = useState(0)
	useEffect(() => {
		setUpdateContent(textToEditorContent(null))
		setComposerKey((key) => key + 1)
	}, [projectId])
	const propertySaving = useRef(false)
	const updates = api.projects.updates.useInfiniteQuery(
		{ teamId: team?.id ?? "", id: projectId ?? "" },
		{
			enabled: !practice && !!team?.enableProjects && !!projectId,
			getNextPageParam: (page) => page.nextCursor,
			refetchInterval: 30_000
		}
	)
	const projectParticipants =
		practice?.participants ?? updates.data?.pages[0]?.participants ?? []
	const projectUpdates =
		practice?.updates ??
		updates.data?.pages.flatMap((page) => page.updates) ??
		[]
	const canPostUpdate =
		practice?.canPost ?? updates.data?.pages[0]?.canPost ?? false
	const postUpdate = api.projects.postUpdate.useMutation()
	const setParticipant = api.projects.setParticipant.useMutation({
		onError: (error) => toast.error(error.message),
		onSuccess: () =>
			utils.projects.updates.invalidate({
				teamId: team?.id ?? "",
				id: projectId ?? ""
			})
	})
	const properties = api.projects.updateProperties.useMutation()
	const save = api.projects.save.useMutation()
	const createdProjectId = !projectId && save.data?.id
	const tabIsDirty = workspace?.dirtyTabIds.includes(workspaceTab?.id ?? "")
	useEffect(() => {
		if (!createdProjectId || draft || tabIsDirty || !team?.enableProjects)
			return
		let canceled = false
		// React Router refreshes the parent's blocker in an effect after this one.
		queueMicrotask(() => {
			if (!canceled)
				void navigate(`/${orgSlug}/${team.slug}/projects/${createdProjectId}`)
		})
		return () => {
			canceled = true
		}
	}, [
		createdProjectId,
		draft,
		tabIsDirty,
		team?.enableProjects,
		team?.slug,
		orgSlug,
		navigate
	])
	const remove = api.projects.delete.useMutation({
		onError: (error) => toast.error(error.message)
	})
	const editIssue = api.issues.edit.useMutation({
		onError: (error) => toast.error(error.message),
		onSuccess: async () => {
			await utils.issues.getTeamIssues.invalidate()
			await utils.issues.get.invalidate()
			await utils.projects.list.invalidate()
		}
	})
	api.issues.onUpdate.useSubscription(
		{ teamId: team?.id ?? "" },
		{
			enabled: !practice && !!team?.enableProjects,
			onData: () => {
				void utils.issues.getTeamIssues.invalidate()
				void utils.projects.list.invalidate()
			}
		}
	)
	if (isLoading) return <LoadingState />
	if (!team?.enableProjects) return <EmptyState title="Projects are disabled" />
	if (projectId && project.isLoading) return <LoadingState />
	if (!team) return <EmptyState title="Team not found" />
	if (projectId && (project.error || !project.data))
		return (
			<ErrorState
				title="Unable to load project"
				description={project.error?.message ?? "Project not found"}
			/>
		)
	const base = `/${orgSlug}/${team.slug}/projects`
	const initial = {
		teamId: team.id,
		...(projectId && project.data
			? project.data
			: {
					name: "",
					description: "",
					status: ProjectStatus.Planned,
					leadId: null,
					startDate: null,
					targetDate: null,
					resources: []
				})
	}
	const values =
		draft && draft.id === projectId && draft.teamId === team.id
			? draft
			: initial
	const dirty = JSON.stringify(values) !== JSON.stringify(initial)
	const canEdit = !!practice || hasTeamPermission(TeamPermissions.EDIT_ISSUES)
	const canCreate =
		!practice &&
		(hasTeamPermission(TeamPermissions.CREATE_ISSUES) ||
			hasTeamPermission(TeamPermissions.CREATE_QUEUE_ISSUES))
	const busy = save.isPending || remove.isPending || properties.isPending
	const activeIssues =
		issues.data?.issues.filter(
			(issue) => issue.status !== IssueStatus.Canceled
		) ?? []
	const completed = activeIssues.filter(
		(issue) => issue.status === IssueStatus.Complete
	).length
	const progress = activeIssues.length
		? (completed / activeIssues.length) * 100
		: 0
	const saveProperties = async (
		changes: RouterInputs["projects"]["updateProperties"]["changes"]
	) => {
		if (!canEdit || busy || propertySaving.current) return
		const nextStart =
			(changes.startDate === undefined
				? values.startDate
				: changes.startDate) ??
			project.data?.createdAt ??
			new Date()
		const nextDue =
			changes.targetDate === undefined ? values.targetDate : changes.targetDate
		if (nextDue && nextDue < nextStart) {
			toast.error("The project due date cannot be before its start date.")
			return
		}
		if (!projectId || !project.data) {
			setDraft({ ...values, ...changes })
			return
		}
		if (
			!practice &&
			draft?.updatedAt &&
			draft.updatedAt.getTime() !== project.data.updatedAt.getTime()
		) {
			toast.error(
				"This project changed while you were editing. Save or reset your draft before changing properties."
			)
			return
		}
		propertySaving.current = true
		try {
			if (practice) {
				await practice.save({ teamId: team.id, ...project.data, ...changes })
				setDraft((current) =>
					current
						? {
								...current,
								...changes,
								startDate:
									changes.startDate === null
										? practice.createdAt
										: (changes.startDate ?? current.startDate)
							}
						: null
				)
			} else {
				const saved = await properties.mutateAsync({
					id: projectId,
					teamId: team.id,
					updatedAt: project.data.updatedAt,
					changes
				})
				utils.projects.get.setData(
					{ id: projectId, teamId: team.id },
					(current) => (current ? { ...current, ...saved } : current)
				)
				setDraft((current) => (current ? { ...current, ...saved } : null))
				void utils.projects.list.invalidate()
				void utils.projects.updates.invalidate({
					id: projectId,
					teamId: team.id
				})
			}
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: "Unable to save project properties"
			)
		} finally {
			propertySaving.current = false
		}
	}
	const details = (
		<Card variant="outline" density="flush">
			<DetailsSection title="Properties" variant="grouped">
				<fieldset
					data-onboarding-target="project-properties"
					disabled={!canEdit || busy}
					className="flex flex-col gap-4"
				>
					<DetailsRow label="Status">
						<IssueSetting
							ariaLabel="Project status"
							value={values.status}
							onChange={(status) => void saveProperties({ status })}
							disabled={!canEdit || busy}
							showArrow
							showBorder
							options={projectStatusValues.map((value) => ({
								value,
								searchText: enumFormat(value),
								display: <ProjectStatusIcon status={value} withText />
							}))}
						/>
					</DetailsRow>
					<DetailsRow label="Lead">
						<IssueSetting
							ariaLabel="Project lead"
							value={values.leadId}
							onChange={(leadId) => void saveProperties({ leadId })}
							disabled={!canEdit || busy}
							showArrow
							showBorder
							withSearch
							options={
								practice
									? [
											{ value: null, display: "Unassigned" },
											...practice.participants.map((member) => ({
												value: member.id,
												searchText: member.name,
												display: (
													<EntityIdentity
														kind="user"
														name={member.name}
														image={member.image}
														size="sm"
													/>
												)
											}))
										]
									: issueSettingAssignees(members)
							}
						/>
					</DetailsRow>
					<DetailsRow label="Start date">
						<IssueDueDatePicker
							ariaLabel="Project start date"
							maxDate={values.targetDate}
							value={values.startDate}
							onChange={(startDate) => void saveProperties({ startDate })}
							disabled={!canEdit || busy}
							showBorder
							emptyLabel="Date created"
							clearLabel="Reset to date created"
						/>
					</DetailsRow>
					<DetailsRow label="Due date">
						<IssueDueDatePicker
							ariaLabel="Project due date"
							minDate={
								values.startDate ?? project.data?.createdAt ?? new Date()
							}
							value={values.targetDate}
							onChange={(targetDate) => void saveProperties({ targetDate })}
							disabled={!canEdit || busy}
							showBorder
						/>
					</DetailsRow>
				</fieldset>
			</DetailsSection>
			{projectId && (
				<DetailsSection title="Members" variant="grouped">
					{!practice && updates.isError ? (
						<span className="text-sm text-text-sub">
							Unable to load members.
						</span>
					) : (
						<Popover>
							<PopoverTrigger asChild>
								<Button
									data-onboarding-target="project-members"
									variant="picker-outline"
									size="sm"
									disabled={!practice && updates.isLoading}
									aria-label="Project members"
								>
									<UsersIcon className="size-4" />
									{projectParticipants.filter((member) => member.included)
										.length ?? 0}{" "}
									{projectParticipants.filter((member) => member.included)
										.length === 1
										? "member"
										: "members"}
								</Button>
							</PopoverTrigger>
							<PopoverContent
								layout="panel"
								align="end"
								aria-label="Project members"
							>
								<Command>
									<CommandInput placeholder="Find a team member…" />
									<CommandList>
										<CommandEmpty>No members found</CommandEmpty>
										<CommandGroup
											heading={
												canEdit
													? "Members receive project updates"
													: "Project members"
											}
										>
											{projectParticipants
												.filter((member) => canEdit || member.included)
												.map((member) => (
													<CommandItem
														key={member.id}
														value={`${member.id} ${member.name}`}
														disabled={!canEdit || setParticipant.isPending}
														onSelect={() =>
															(practice
																? practice.setParticipant
																: setParticipant.mutate)({
																teamId: team.id,
																id: projectId,
																teamMemberId: member.id,
																included: !member.included
															})
														}
														aria-label={`${member.included ? "Remove" : "Add"} ${member.name}`}
													>
														<EntityIdentity
															kind="user"
															name={member.name}
															image={member.image}
															userId={member.userId}
															size="sm"
														/>
														{member.included && (
															<CheckIcon className="ml-auto size-4" />
														)}
													</CommandItem>
												))}
										</CommandGroup>
									</CommandList>
								</Command>
							</PopoverContent>
						</Popover>
					)}
				</DetailsSection>
			)}

			{projectId && (
				<DetailsSection title="Progress" variant="grouped">
					<div
						data-onboarding-target="project-progress"
						className="flex flex-col gap-3"
					>
						<span className="text-sm">
							{completed}/{activeIssues.length} complete ·{" "}
							{Math.round(progress)}%
						</span>
						<Progress aria-label="Project completion" value={progress} />
						<span className="text-xs text-text-sub">
							Canceled issues excluded.
						</span>
					</div>
				</DetailsSection>
			)}
		</Card>
	)
	return (
		<main className="flex h-full min-h-0 flex-col">
			{tutorialPrompt}
			<DetailsLayout
				title="Project details"
				compactMode={practice ? "inline" : "sheet"}
				details={details}
				header={(toggle) => (
					<AppHeader
						contentClassName="flex items-center gap-2"
						actions={
							<>
								{toggle}
								{tutorialActions}
								{projectId && canEdit && !practice && (
									<Button
										variant="ghost"
										size="icon"
										aria-label="Delete project"
										disabled={busy || dirty}
										onClick={async () => {
											if (
												!project.data ||
												!(await confirmAction({
													title: "Delete project",
													message: `Delete ${project.data.name}? Its issues will be kept without a project.`
												}))
											)
												return
											await remove.mutateAsync({
												teamId: team.id,
												id: project.data.id,
												updatedAt: project.data.updatedAt
											})
											await utils.projects.list.invalidate()
											await utils.issues.getTeamIssues.invalidate()
											await utils.issues.get.invalidate()
											navigate(base)
										}}
									>
										<Trash2Icon className="size-4" />
									</Button>
								)}
							</>
						}
					>
						{practice ? (
							<span>Biteclub / Projects</span>
						) : (
							<Link className="text-text-sub hover:underline" to={base}>
								Projects
							</Link>
						)}
						<span className="text-text-sub">/</span>
						<span className="truncate font-medium">
							{project.data?.name ?? "New project"}
						</span>
					</AppHeader>
				)}
			>
				{(compact) => (
					<div className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-6 pb-44 sm:px-6">
						<fieldset
							data-onboarding-target="project-summary"
							disabled={!canEdit || busy}
							className="flex min-w-0 flex-col gap-1"
						>
							<Input
								aria-label="Project name"
								variant="title"
								placeholder="Project name"
								maxLength={100}
								value={values.name}
								onChange={(event) =>
									setDraft({ ...values, name: event.target.value })
								}
							/>
							<Textarea
								aria-label="Project summary"
								variant="summary"
								placeholder="Project summary…"
								maxLength={5000}
								value={values.description}
								onChange={(event) =>
									setDraft({ ...values, description: event.target.value })
								}
							/>
						</fieldset>

						{compact && practice && details}
						{projectId && (
							<section
								className="flex flex-col gap-4"
								id="project-updates"
								data-onboarding-target="project-updates"
							>
								<h2 className="text-sm font-semibold">Updates</h2>
								{!practice && updates.isLoading ? (
									<LoadingState />
								) : !practice && updates.isError ? (
									<ErrorState
										title="Unable to load updates"
										description={updates.error.message}
									/>
								) : (
									<>
										{canPostUpdate && (
											<CommentComposer
												key={composerKey}
												value={updateContent}
												onChange={setUpdateContent}
												isPending={postUpdate.isPending}
												referencesEnabled={!practice}
												mentionUsers={practice ? [] : undefined}
												placeholder="Share progress, blockers, or what's coming next…"
												submitLabel="Post update"
												onSubmit={async () => {
													await (
														practice
															? practice.postUpdate
															: postUpdate.mutateAsync
													)({
														teamId: team.id,
														id: projectId,
														content: updateContent
													})
													setUpdateContent(textToEditorContent(null))
													setComposerKey((key) => key + 1)
													if (!practice)
														await utils.projects.updates.invalidate({
															teamId: team.id,
															id: projectId
														})
													toast.success(
														practice
															? "Practice update posted — no notifications sent"
															: "Project update posted"
													)
												}}
											/>
										)}
										{!projectUpdates.length && (
											<p className="text-sm text-text-sub">
												{canPostUpdate
													? "Share your first update with the project members."
													: "The project lead hasn't posted an update yet."}
											</p>
										)}
										{projectUpdates.map((update) => (
											<Card
												key={update.id}
												variant="outline"
												density="compact"
												id={`project-update-${update.id}`}
											>
												<div className="flex items-center gap-2 text-sm">
													<EntityIdentity
														kind="user"
														name={update.author?.name ?? "Deleted user"}
														image={update.author?.image}
														userId={update.author?.id}
														size="sm"
													/>
													<time
														className="text-xs text-text-sub"
														dateTime={update.createdAt.toISOString()}
														title={update.createdAt.toLocaleString()}
													>
														{formatDistanceToNow(update.createdAt, {
															addSuffix: true
														})}
													</time>
												</div>
												<CommentContent
													content={update.content}
													referencesEnabled={!practice}
												/>
											</Card>
										))}
										{!practice && updates.hasNextPage && (
											<Button
												variant="ghost"
												disabled={updates.isFetchingNextPage}
												onClick={() => void updates.fetchNextPage()}
											>
												{updates.isFetchingNextPage
													? "Loading…"
													: "Load older updates"}
											</Button>
										)}
									</>
								)}
							</section>
						)}

						{projectId && (
							<section
								data-onboarding-target="project-issues"
								className="flex flex-col gap-4"
							>
								<div className="grid min-w-0 gap-3 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center">
									<h2 className="text-sm font-semibold">
										Issues{" "}
										<span className="text-text-sub">
											{issues.data?.issues.length ?? 0}
										</span>
									</h2>
									<div className="flex min-w-0 items-center gap-2 sm:justify-self-end">
										{canEdit && (
											<div className="min-w-0 flex-1 sm:w-44 sm:flex-none">
												<IssueSetting<string | null>
													ariaLabel="Add existing issue"
													popoverSize="xl"
													searchPlaceholder="Search by issue ID or title…"
													customNotFound="No matching issues"
													value={null}
													placeholder="Add existing issue"
													disabled={
														editIssue.isPending ||
														allIssues.isLoading ||
														!!allIssues.error
													}
													withSearch
													showArrow
													showBorder
													options={(
														allIssues.data?.issues.filter(
															(issue) => issue.projectId !== projectId
														) ?? []
													).map((issue) => ({
														value: issue.id,
														searchText: `${fancyIssueId(issue.friendlyId, team.slug)} ${issue.title}`,
														display: (
															<IssuePickerOption
																identifier={fancyIssueId(
																	issue.friendlyId,
																	team.slug
																)}
																title={issue.title}
																status={issue.status}
																moving={!!issue.projectId}
															/>
														)
													}))}
													onChange={(id) => {
														if (id)
															practice
																? practice.editIssue({
																		id,
																		teamId: team.id,
																		projectId
																	})
																: editIssue.mutate({
																		id,
																		teamId: team.id,
																		projectId
																	})
													}}
												/>
											</div>
										)}
										{canCreate && (
											<IssueCreateDialog
												presetTeamId={team.id}
												presetProjectId={projectId}
											>
												<Button size="sm">
													<PlusIcon className="size-4" /> New issue
												</Button>
											</IssueCreateDialog>
										)}
									</div>
								</div>
								{issues.isLoading ? (
									<LoadingState />
								) : issues.error ? (
									<ErrorState
										title="Unable to load issues"
										description={issues.error.message}
									/>
								) : issues.data?.issues.length ? (
									<Card variant="outline" density="flush">
										<div className="divide-y divide-stroke">
											{issues.data.issues.map((issue) => (
												<div
													key={issue.id}
													className="flex min-w-0 items-center"
												>
													<div className="min-w-0 flex-1">
														<IssueRow
															practice={!!practice}
															onOpen={
																practice
																	? () =>
																			toast.info(
																				"This tour focuses on projects. Try the Welcome to Orbit tour to edit a practice issue."
																			)
																	: undefined
															}
															issue={issue}
															hiddenProperties={[
																"labels",
																"priority",
																"difficulty",
																"updatedAt"
															]}
														/>
													</div>
													{canEdit && (
														<div className="shrink-0 pr-2">
															<TooltipButton
																tooltip={`Remove ${fancyIssueId(issue.friendlyId, team.slug)} from project`}
																variant="ghost"
																size="icon-sm"
																disabled={editIssue.isPending}
																onClick={() =>
																	(practice
																		? practice.editIssue
																		: editIssue.mutate)({
																		id: issue.id,
																		teamId: team.id,
																		projectId: null
																	})
																}
															>
																<XIcon className="size-4" />
															</TooltipButton>
														</div>
													)}
												</div>
											))}
										</div>
									</Card>
								) : (
									<EmptyState
										scope="inline"
										title="No issues in this project"
									/>
								)}
							</section>
						)}
						<section
							data-onboarding-target="project-resources"
							className="flex min-w-0 flex-col gap-3 border-t border-stroke pt-6"
						>
							<div className="flex items-center justify-between gap-2">
								<h2 className="text-sm font-semibold">Resources</h2>
								{canEdit && (
									<Button
										variant="ghost"
										size="sm"
										disabled={busy || values.resources.length >= 20}
										onClick={() =>
											setDraft({
												...values,
												resources: [...values.resources, { title: "", url: "" }]
											})
										}
									>
										<PlusIcon className="size-4" /> Add link
									</Button>
								)}
							</div>
							{values.resources.map((resource, index) => (
								<div
									key={index}
									className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2"
								>
									{canEdit ? (
										<div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
											<Input
												aria-label={`Resource ${index + 1} title`}
												placeholder="Link title"
												value={resource.title}
												disabled={busy}
												onChange={(event) =>
													setDraft({
														...values,
														resources: values.resources.map((item, i) =>
															i === index
																? { ...item, title: event.target.value }
																: item
														)
													})
												}
											/>
											<Input
												aria-label={`Resource ${index + 1} URL`}
												type="url"
												placeholder="https://…"
												value={resource.url}
												disabled={busy}
												onChange={(event) =>
													setDraft({
														...values,
														resources: values.resources.map((item, i) =>
															i === index
																? { ...item, url: event.target.value }
																: item
														)
													})
												}
											/>
										</div>
									) : (
										<span className="truncate text-sm">
											{resource.title || resource.url}
										</span>
									)}
									<div className="flex shrink-0 items-center gap-1">
										{!practice && /^https?:\/\//i.test(resource.url) && (
											<TooltipButton
												asChild
												tooltip={`Open ${resource.title || "resource"}`}
												variant="ghost"
												size="icon-sm"
											>
												<Link
													to={resource.url}
													target="_blank"
													rel="noopener noreferrer"
												>
													<ExternalLinkIcon className="size-4" />
												</Link>
											</TooltipButton>
										)}
										{canEdit && (
											<TooltipButton
												tooltip={`Remove resource ${index + 1}`}
												variant="ghost"
												size="icon-sm"
												disabled={busy}
												onClick={() =>
													setDraft({
														...values,
														resources: values.resources.filter(
															(_, i) => i !== index
														)
													})
												}
											>
												<XIcon className="size-4" />
											</TooltipButton>
										)}
									</div>
								</div>
							))}
						</section>
					</div>
				)}
			</DetailsLayout>
			<UnsavedChanges
				protectNavigation={!practice}
				isVisible={canEdit && dirty}
				pending={busy}
				canSave={projectSchema.safeParse(values).success}
				onReset={async () => {
					if (projectId && !practice) await project.refetch()
					setDraft(null)
				}}
				onSave={async () => {
					if (practice) await practice.save(projectSchema.parse(values))
					else {
						await save.mutateAsync(projectSchema.parse(values))
						await utils.projects.invalidate()
					}
					setDraft(null)
					toast.success(
						practice
							? "Practice changes saved"
							: projectId
								? "Project saved"
								: "Project created"
					)
				}}
			/>
		</main>
	)
}
